import { ChevronRightIcon } from '@heroicons/react/24/outline';
import type { ColumnType } from 'antd/es/table';
import Tooltip from 'antd/es/tooltip';

import { sortByText } from 'utils/antd/sorters';

import { RoleLabel } from '@pages/UserManagement/shared/RoleLabel';
import type { UserItem } from 'api';

import { ColumnTitle } from '@atoms/ColumnTitle';
import { TableSortIcon } from '@atoms/Icon/TableSortIcon';

import { userManagementT as translations } from './translations';

const COL_WIDTH = {
  SPACER: 24,
  CHEVRON: 48,
  ROLE: 280
} as const;

export function getColumns(): ColumnType<UserItem>[] {
  const sortableHeader = {
    sortIcon: ({ sortOrder }: { sortOrder?: 'ascend' | 'descend' | null }) => (
      <TableSortIcon sortOrder={sortOrder} />
    ),
    onHeaderCell: () => ({ className: 'align-sorter-icon' })
  };

  return [
    {
      key: 'empty_space',
      width: COL_WIDTH.SPACER
    },
    {
      ...sortableHeader,
      title: <ColumnTitle title={translations.table.columns.name} />,
      dataIndex: 'last_name',
      key: 'name',
      render: (_, record: UserItem) => (
        <Tooltip title={`${record.first_name} ${record.last_name}`}>
          <div className="inline-block max-w-full truncate">
            {record.first_name} {record.last_name}
          </div>
        </Tooltip>
      ),
      sorter: (a, b) =>
        sortByText(
          `${a.last_name} ${a.first_name}`,
          `${b.last_name} ${b.first_name}`
        )
    },
    {
      ...sortableHeader,
      title: <ColumnTitle title={translations.table.columns.email} />,
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => (
        <Tooltip title={email}>
          <div className="inline-block max-w-full truncate">{email}</div>
        </Tooltip>
      ),
      sorter: (a, b) => sortByText(a.email, b.email)
    },
    {
      ...sortableHeader,
      title: <ColumnTitle title={translations.table.columns.role} />,
      dataIndex: 'role',
      key: 'role',
      width: COL_WIDTH.ROLE,
      render: (role: string) => <RoleLabel role={role} />,
      sorter: (a, b) => sortByText(a.role, b.role)
    },
    {
      dataIndex: 'icon',
      key: 'arrowIcon',
      align: 'right',
      width: COL_WIDTH.CHEVRON,
      render: () => (
        <ChevronRightIcon className="h-6 stroke-content-subtle group-hover:stroke-content-primary group-active:stroke-content-primary" />
      )
    }
  ];
}
